import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Auth } from "aws-amplify";
import Modal from "../generic/modal";
import { formatBalance, stripeAccountChip } from "../../helpers/functions";
import { RevBarra, RevButton, RevCard, RevChip, RevDanger, RevField, RevKvRow, RevSegmented, RevSwitch } from "../ds";

// E.164, como lo guardan Cognito y el backend; se aceptan espacios, guiones y paréntesis al teclear.
const normalizePhone = (value) => (value || '').replace(/[\s().-]/g, '');
const E164 = /^\+[1-9]\d{6,14}$/;

const AccountForm = ({ user, postUser, deleteAccount, signOut, redirect, events, getUser, ...props }) => {
    const { t, i18n } = useTranslation();
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);
    const [pending, setPending] = useState(null);
    const [saved, setSaved] = useState(false);

    const { register, watch, setValue, handleSubmit, reset, formState: { isDirty, errors } } = useForm({
        defaultValues: {
            "id": user?.id,
            "name": user?.name,
            "email": user?.email,
            "phone": user?.phone || '',
            "receiveNews": user?.receiveNews,
            "lang": user?.lang,
            "currency": user?.currency,
            "role": "activeUser"
        }
    });

    useEffect(() => {
        if (!user) return;
        if (isDirty && !pending) return;
        reset({
            "id": user?.id,
            "name": user?.name,
            "email": user?.email,
            "phone": user?.phone || '',
            "receiveNews": user?.receiveNews,
            "lang": user?.lang,
            "currency": user?.currency,
            "role": "activeUser"
        });
        // eslint-disable-next-line
    }, [user]);

    useEffect(() => {
        if (!pending || !user) return;
        // El `user` de redux tiene que haber sido REEMPLAZADO desde el envío (POST_USER
        // sustituye el objeto entero): comparar solo por valor confirmaría un guardado
        // fallido cuando lo enviado ya coincidía con lo que había — p. ej. `receiveNews`
        // de undefined a false, donde Boolean(undefined) === Boolean(false).
        if (user === pending.user) return;
        const sent = pending.data;
        const matches = user.name === sent.name
            && (sent.phone === undefined || user.phone === sent.phone)
            && user.lang === sent.lang
            && Boolean(user.receiveNews) === Boolean(sent.receiveNews);
        if (!matches) return;
        setSaved(true);
        getUser();
        setPending(null);
        // eslint-disable-next-line
    }, [user, pending]);

    useEffect(() => {
        if (!saved) return;
        let alive = true;
        const timer = setTimeout(() => {
            if (alive) setSaved(false);
        }, 4000);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [saved]);

    const onSubmit = (form) => {
        // Vacío no se envía: el backend conserva el que hubiera (no se puede borrar).
        const phone = normalizePhone(form.phone) || undefined;
        const data = { ...form, phone };
        setPending({ data, user });
        // Si el guardado falla hay que soltar el `pending`: si no, la guarda del `reset`
        // de arriba (que solo cede cuando hay un guardado en vuelo) se queda apagada de
        // por vida y el `reset` vuelve a poder pisar lo tecleado.
        postUser(data).then((ok) => {
            if (!ok) {
                setPending(null);
                return;
            }
            // Cognito guarda también el teléfono (phone_number); si falla, Dynamo ya lo tiene.
            if (phone && phone !== user?.phone) {
                Auth.currentAuthenticatedUser()
                    .then((cognitoUser) => Auth.updateUserAttributes(cognitoUser, { phone_number: phone }))
                    .catch((error) => console.warn('phone_number not synced to Cognito', error));
            }
        });
    };

    const onConfirmDelete = () => {
        setLoadingModal(true);
        deleteAccount().then(() => {
            signOut().then(() => {
                setLoadingModal(false);
            });
        }).catch((e) => alert(e));
    };

    const onError = (err, e) => {
        const primeraClave = Object.keys(err)[0];
        const mensaje = err[primeraClave]?.message ?? "Error desconocido";
        console.log(mensaje);
    };

    const activeEventsCount = (events || []).filter(e => e.active === true).length;

    const chip = stripeAccountChip(user);
    const stripeChip = <RevChip tone={chip.tone}>{t(chip.key)}</RevChip>;

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
                {user && <>
                    <div className="rev-cols rev-cols--flush">
                        <div className="rev-cols__main">
                            <RevCard title={t('account.profile.data.title')}>
                                <div className="rev-form-grid">
                                    <RevField label={t('account.basicData.name.label')} htmlFor="name">
                                        <input id="name" className="rev-input" type="text" {...register('name')} />
                                    </RevField>
                                    <RevField label={t('account.basicData.email.label')} htmlFor="email">
                                        <input id="email" className="rev-input rev-input--readonly" type="text" disabled {...register('email')} />
                                    </RevField>
                                    <RevField label={t('account.profile.phone.label')} htmlFor="phone"
                                        error={errors.phone ? t('account.profile.phone.invalid') : undefined}>
                                        <input id="phone" data-testid="phone" className="rev-input" type="tel" autoComplete="tel"
                                            placeholder="+34 600 123 456"
                                            {...register('phone', {
                                                validate: (value) => !normalizePhone(value) || E164.test(normalizePhone(value)),
                                            })} />
                                    </RevField>
                                </div>
                                <div className="rev-form-grid rev-form-grid--divider">
                                    <RevField label={t('account.profile.news.label')}>
                                        <label className="rev-switch-field" htmlFor="receiveNews">
                                            <input
                                                type="checkbox"
                                                id="receiveNews"
                                                data-testid="receiveNews"
                                                className="rev-switch-input"
                                                {...register("receiveNews")} />
                                            <RevSwitch on={watch('receiveNews')} />
                                            <span>{t('account.profile.news.switch')}</span>
                                        </label>
                                    </RevField>
                                    <RevField label={t('account.profile.lang.label')} help={t('account.profile.lang.help')}>
                                        <input type="hidden" data-testid="lang" {...register('lang')} />
                                        <RevSegmented
                                            items={[{ value: 'es', label: 'Español' }, { value: 'en', label: 'English' }]}
                                            value={watch('lang')}
                                            onChange={(lang) => setValue('lang', lang, { shouldDirty: true })}
                                        />
                                    </RevField>
                                </div>
                            </RevCard>
                            <RevDanger
                                title={t('Delete my account')}
                                text={t('account.profile.delete.text')}
                                action={
                                    <RevButton data-testid="deleteAccount" variant="danger" onClick={() => setShowDeleteAccountModal(true)}>
                                        {t('account.profile.delete.button')}
                                    </RevButton>
                                }
                            />
                        </div>
                        <div className="rev-cols__aside">
                            <RevCard title={t('account.profile.overview.title')}>
                                <RevKvRow k={t('account.profile.overview.balance')} v={formatBalance(user?.balance, i18n.language)} num />
                                <RevKvRow k={t('account.profile.overview.activeEvents')} v={activeEventsCount} num />
                                <RevKvRow k={t('account.profile.overview.payouts')}>{stripeChip}</RevKvRow>
                            </RevCard>
                        </div>
                    </div>
                    <RevBarra sangre ok={saved ? t('account.profile.saved') : null}>
                        <RevButton data-testid="saveOrCreate" type="submit" variant="primary"
                            disabled={!isDirty || props?.loading} loading={props?.loading}>
                            {t('generic.saveButton.text')}
                        </RevButton>
                    </RevBarra>
                    {showDeleteAccountModal === true &&
                        <Modal
                            isDanger
                            showCancelButton
                            showConfirmButton
                            title={t('Atention!')}
                            message={t('You are about to delete your account. This operation cannot be undone. Are you sure you want to delete your account?')}
                            cancelButtonText={t('generic.alertButton.cancel')}
                            confirmButtonText={t('Yes, delete my account permanently')}
                            onCancel={() => setShowDeleteAccountModal(false)}
                            onConfirm={onConfirmDelete}
                            inputConfirmation={t('DELETE')}
                            confirmationPlaceholder={t("Type '") + t('DELETE') + t("' to continue")}
                            loading={loadingModal}
                        />
                    }

                </>}
            </form>
        </>
    );
}

export default AccountForm;
